import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Tag, Switch, Modal, Form, Input, InputNumber, Select, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiClient } from 'Utils/api';

interface TaxRate {
  id: string;
  name: string;
  countryCodes: string[] | string;
  ratePermille: number;
  isActive: boolean;
}

const EU_COUNTRIES: { code: string; label: string }[] = [
  { code: 'DE', label: 'Deutschland' }, { code: 'AT', label: 'Österreich' }, { code: 'BE', label: 'Belgien' },
  { code: 'BG', label: 'Bulgarien' }, { code: 'HR', label: 'Kroatien' }, { code: 'CY', label: 'Zypern' },
  { code: 'CZ', label: 'Tschechien' }, { code: 'DK', label: 'Dänemark' }, { code: 'EE', label: 'Estland' },
  { code: 'FI', label: 'Finnland' }, { code: 'FR', label: 'Frankreich' }, { code: 'GR', label: 'Griechenland' },
  { code: 'HU', label: 'Ungarn' }, { code: 'IE', label: 'Irland' }, { code: 'IT', label: 'Italien' },
  { code: 'LV', label: 'Lettland' }, { code: 'LT', label: 'Litauen' }, { code: 'LU', label: 'Luxemburg' },
  { code: 'MT', label: 'Malta' }, { code: 'NL', label: 'Niederlande' }, { code: 'PL', label: 'Polen' },
  { code: 'PT', label: 'Portugal' }, { code: 'RO', label: 'Rumänien' }, { code: 'SK', label: 'Slowakei' },
  { code: 'SI', label: 'Slowenien' }, { code: 'ES', label: 'Spanien' }, { code: 'SE', label: 'Schweden' },
];

const parseCodes = (v: string[] | string): string[] => {
  if (Array.isArray(v)) return v;
  try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch { return []; }
};

const TaxRatesTab: React.FC = () => {
  const [rates, setRates] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TaxRate | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/tax-rates');
      setRates(res.data.taxRates || []);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ ratePercent: 19, isActive: true, countryCodes: [] });
    setModalOpen(true);
  };

  const openEdit = (r: TaxRate) => {
    setEditing(r);
    form.setFieldsValue({
      name: r.name,
      ratePercent: r.ratePermille / 10,
      countryCodes: parseCodes(r.countryCodes),
      isActive: r.isActive,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        ratePermille: Math.round(Number(values.ratePercent) * 10),
        countryCodes: values.countryCodes,
        isActive: values.isActive,
      };
      if (editing) {
        await apiClient.patch(`/api/admin/tax-rates/${editing.id}`, payload);
        message.success('Steuersatz aktualisiert');
      } else {
        await apiClient.post('/api/admin/tax-rates', payload);
        message.success('Steuersatz angelegt');
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || 'Fehler beim Speichern');
    }
  };

  const toggleActive = async (r: TaxRate, isActive: boolean) => {
    try {
      await apiClient.patch(`/api/admin/tax-rates/${r.id}`, { isActive });
      load();
    } catch (e: any) {
      message.error('Fehler beim Aktualisieren');
    }
  };

  const remove = async (id: string) => {
    try {
      await apiClient.delete(`/api/admin/tax-rates/${id}`);
      message.success('Steuersatz gelöscht');
      load();
    } catch (e: any) {
      message.error('Fehler beim Löschen');
    }
  };

  const columns = [
    {
      title: 'Steuersatz',
      dataIndex: 'ratePermille',
      key: 'ratePermille',
      width: 130,
      sorter: (a: TaxRate, b: TaxRate) => a.ratePermille - b.ratePermille,
      render: (v: number) => <strong style={{ color: '#b8964a' }}>{(v / 10).toLocaleString('de-DE')} %</strong>,
    },
    { title: 'Bezeichnung', dataIndex: 'name', key: 'name' },
    {
      title: 'Länder (gruppiert)',
      dataIndex: 'countryCodes',
      key: 'countryCodes',
      render: (v: any) => (
        <Space wrap>
          {parseCodes(v).map((c) => <Tag key={c}>{c}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Aktiv',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (active: boolean, r: TaxRate) => (
        <Switch checked={active} onChange={(val) => toggleActive(r, val)} data-testid={`tax-active-${r.id}`} />
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      width: 170,
      render: (_: any, r: TaxRate) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(r)}>Bearbeiten</Button>
          <Popconfirm title="Steuersatz löschen?" onConfirm={() => remove(r.id)} okText="Löschen" cancelText="Abbrechen">
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 12, color: '#555' }}>
        Länder mit <strong>gleichem Steuersatz</strong> werden in einer Zeile zusammengefasst – du musst nicht jedes Land
        einzeln anlegen. Deutschland (19 %) ist der Basissatz; Produktpreise werden als deutscher Bruttopreis eingegeben.
      </div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} data-testid="add-tax-rate-btn">
          Steuersatz / Ländergruppe anlegen
        </Button>
      </div>
      <Table rowKey="id" dataSource={rates} columns={columns as any} loading={loading} pagination={false} />

      <Modal
        title={editing ? 'Steuersatz bearbeiten' : 'Steuersatz anlegen'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText="Speichern"
        cancelText="Abbrechen"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Bezeichnung" name="name" rules={[{ required: true, message: 'Bitte Bezeichnung angeben' }]}>
            <Input placeholder="z. B. Standard 21 %" data-testid="tax-name-input" />
          </Form.Item>
          <Form.Item label="Steuersatz (%)" name="ratePercent" rules={[{ required: true, message: 'Pflichtfeld' }]}>
            <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} data-testid="tax-rate-input" />
          </Form.Item>
          <Form.Item
            label="Länder (ISO-2 Codes)"
            name="countryCodes"
            rules={[{ required: true, message: 'Mindestens ein Land wählen' }]}
            extra="Mehrere Länder mit dem gleichen Satz hier zusammenfassen."
          >
            <Select
              mode="tags"
              placeholder="DE, FR, IT …"
              options={EU_COUNTRIES.map((c) => ({ value: c.code, label: `${c.code} – ${c.label}` }))}
              data-testid="tax-countries-select"
            />
          </Form.Item>
          <Form.Item label="Aktiv" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TaxRatesTab;
